/**
 * The call engine: everything a staff call does that is not drawing it.
 *
 * It polls the platform for rings, calls and signals; makes one
 * RTCPeerConnection per other person on the call (a mesh); holds the
 * microphone and the camera; and tells whoever listens what the call looks
 * like now. It knows nothing of React, so the window, the ring card and the
 * tests all read the same state — and the tests can hand it a fake peer
 * connection and a fake camera.
 *
 * The rules below are the shared contract with the phone app (calls-contract,
 * "Client engine rules"). They are not a matter of taste: if the web and the
 * phone disagree on any of them, the two never connect.
 *
 *  - OFFERER is the peer whose principal id is the smaller string. The larger
 *    one waits for the offer. Two sides both offering — "glare" — is the
 *    classic way a mesh fails, and an ordering both sides can compute alone
 *    removes it without a round trip.
 *  - Every connection always carries one audio and one video transceiver, so
 *    switching Voice ↔ Video never renegotiates: the camera is put on or taken
 *    off with replaceTrack, and the others read `video_on` from the poll to
 *    show a face or a picture.
 *  - ICE candidates that arrive before the remote description are queued:
 *    addIceCandidate before setRemoteDescription throws, and the poll returns
 *    signals in batches, so an offer and its first candidates often land in
 *    the same poll — or the candidates first.
 *  - A `hangup` signal, or a participant shown as left or dropped, closes that
 *    connection. A connection that fails is closed; the offerer tries once
 *    more after two seconds, because one retry rescues a flaky network and
 *    an endless loop of them hides a dead one.
 *
 * Polling, not a socket: the platform's database connection budget cannot
 * hold one open per person (contract, top). The cadence is quick while
 * somebody is ringing or talking, slow while idle, and slower still with the
 * page hidden, where a ring is still caught within fifteen seconds.
 */
import type {
  Call,
  CallMedia,
  CallMessage,
  CallPoll,
  CallSettingsPatch,
  CallStartOptions,
  ControlPayload,
  DndState,
  IceConfig,
  Meeting,
  Participant,
  ParticipantRole,
  Signal,
} from "../../domain/calls";

/* ── what the engine talks to ─────────────────────────────────────────── */

export interface CallTransport {
  poll(after: number, afterMsg?: number): Promise<CallPoll>;
  create(invitees: string[], media: CallMedia, opts?: CallStartOptions): Promise<{ call: Call; notes: string[] }>;
  answer(callId: string, video: boolean): Promise<Call>;
  decline(callId: string): Promise<Call>;
  leave(callId: string): Promise<Call>;
  invite(callId: string, invitees: string[]): Promise<Call>;
  signal(callId: string, to: string, kind: "offer" | "answer" | "ice", payload: unknown): Promise<void>;
  media(callId: string, audioOn: boolean, videoOn: boolean, sharing?: boolean): Promise<Call>;
  ice(): Promise<IceConfig>;
  /* v2 — optional, so a transport written for v1 (and the v1 tests) still fits */
  join?(callId: string): Promise<{ call: Call; state: "joined" | "waiting" }>;
  startMeeting?(meetingId: string): Promise<{ meeting: Meeting; call: Call; notes?: string[] }>;
  settings?(callId: string, patch: CallSettingsPatch): Promise<Call>;
  admit?(callId: string, who: string, allow: boolean): Promise<Call>;
  remove?(callId: string, who: string): Promise<Call | undefined>;
  mute?(callId: string, who: string | "all"): Promise<Call | undefined>;
  role?(callId: string, who: string, role: ParticipantRole): Promise<Call | undefined>;
  end?(callId: string): Promise<Call | undefined>;
  hand?(callId: string, up: boolean): Promise<Call>;
  messages?(callId: string, after: number): Promise<CallMessage[]>;
  sendMessage?(callId: string, text: string): Promise<CallMessage>;
}

/* The slice of the WebRTC API the engine uses, so a test can supply a fake. */
export interface SenderLike {
  replaceTrack(track: MediaStreamTrack | null): Promise<void>;
}
export interface TransceiverLike {
  direction: string;
  sender: SenderLike;
  receiver: { track: { kind: string } | null };
}
export interface DescriptionLike {
  type: string;
  sdp?: string;
}
export interface PeerLike {
  addTransceiver(kind: "audio" | "video", init: { direction: "sendrecv" }): TransceiverLike;
  getTransceivers(): TransceiverLike[];
  createOffer(): Promise<DescriptionLike>;
  createAnswer(): Promise<DescriptionLike>;
  setLocalDescription(d: DescriptionLike): Promise<void>;
  setRemoteDescription(d: DescriptionLike): Promise<void>;
  addIceCandidate(c: unknown): Promise<void>;
  close(): void;
  readonly remoteDescription: unknown;
  readonly connectionState: string;
  onicecandidate: ((e: { candidate: { toJSON(): unknown } | null }) => void) | null;
  ontrack: ((e: { track: MediaStreamTrack }) => void) | null;
  onconnectionstatechange: (() => void) | null;
}

export interface EngineDeps {
  transport: CallTransport;
  createPeer?: (config: { iceServers: IceConfig["iceServers"] }) => PeerLike;
  getUserMedia?: (c: { audio: boolean; video: boolean }) => Promise<MediaStream>;
  /** the screen, a window or a tab; absent where the browser cannot share (phones) */
  getDisplayMedia?: (() => Promise<MediaStream>) | null;
  newStream?: () => MediaStream | null;
  isHidden?: () => boolean;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (h: unknown) => void;
  now?: () => number;
  /** who the person is, when neither the poll nor the call says — the provider asks the directory */
  guessMe?: (call: Call) => string | undefined;
}

/* ── what the engine says ─────────────────────────────────────────────── */

export type PeerStatus = "connecting" | "connected" | "reconnecting" | "failed";

export interface PeerView {
  status: PeerStatus;
  stream: MediaStream | null;
}

export interface EngineState {
  /** false once the platform refuses this sign-in staff calls */
  available: boolean;
  me?: string;
  /** calls ringing for me */
  incoming: Call[];
  /** the call I am on, or ringing out */
  call: Call | null;
  /** my side of it; `screen` is what I share while `sharing` */
  local: {
    stream: MediaStream | null;
    audioOn: boolean;
    videoOn: boolean;
    mode: CallMedia;
    sharing?: boolean;
    screen?: MediaStream | null;
  };
  peers: Record<string, PeerView>;
  /** sentences the window shows: a refused camera, a level without video */
  notes: string[];
  /** a refusal from the last thing the person pressed */
  error?: string;
  /** the call that just ended, for a moment's "Call ended" */
  ended?: Call;
  /** a request is in flight from a button */
  busy: boolean;
  /* ── v2 ── */
  /** the chat on my call, oldest first */
  messages: CallMessage[];
  /** the last message id I have seen in the chat panel */
  chatRead: number;
  /** I asked to join and am in the lobby */
  waiting: Call | null;
  /** my own Do not disturb, as the poll says it */
  dnd?: DndState;
  /** my meetings that are live or about to start */
  meetings: Meeting[];
  /** a sentence for a moment: a host muted me, I was let in, a call was held by Do not disturb */
  notice?: { text: string; vars?: Record<string, string> };
}

export const CADENCE = { inCall: 1500, idle: 3000, hidden: 15000 } as const;
const NOTICE_MS = 6000;
const RETRY_AFTER_MS = 2000;

interface PeerEntry {
  pc: PeerLike;
  offerer: boolean;
  retried: boolean;
  queue: unknown[];
  stream: MediaStream | null;
  status: PeerStatus;
  audio?: TransceiverLike;
  video?: TransceiverLike;
}

/** The smaller principal id offers (string compare, as the phone does). */
export function isOfferer(me: string, other: string): boolean {
  return me < other;
}

function message(e: unknown): string {
  const d = (e as { detail?: unknown })?.detail;
  if (typeof d === "string" && d) return d;
  return e instanceof Error ? e.message : String(e);
}

function status(e: unknown): number | undefined {
  return (e as { status?: number })?.status;
}

export class CallEngine {
  private readonly d: Required<Omit<EngineDeps, "guessMe">> & Pick<EngineDeps, "guessMe">;
  private state: EngineState = {
    available: true,
    incoming: [],
    call: null,
    local: { stream: null, audioOn: true, videoOn: false, mode: "audio", sharing: false, screen: null },
    peers: {},
    notes: [],
    busy: false,
    messages: [],
    chatRead: 0,
    waiting: null,
    meetings: [],
  };
  private readonly listeners = new Set<(s: EngineState) => void>();
  private after = 0;
  private timer: unknown = null;
  private running = false;
  private polling = false;
  private readonly peers = new Map<string, PeerEntry>();
  /** peers whose connection failed after its one retry: not offered again until they rejoin */
  private readonly failed = new Set<string>();
  /** polls sent so far, and how many had been sent when my call was created */
  private sent = 0;
  private startedAfter = -1;
  private iceConfig: IceConfig | null = null;
  /** the call I started and have not yet seen in a poll */
  private startedId: string | null = null;
  /** calls I left, so a poll answered before the leave is not taken as me rejoining */
  private readonly left = new Set<string>();
  /** signals are worked one at a time, in order */
  private chain: Promise<void> = Promise.resolve();
  private endedTimer: unknown = null;
  private noticeTimer: unknown = null;
  /** chat lines read so far */
  private afterMsg = 0;
  /** the call whose earlier chat has been fetched */
  private chatFor: string | null = null;
  /** the screen track on every video sender while I share */
  private shareTrack: MediaStreamTrack | null = null;
  private shareEnded: (() => void) | null = null;
  /** polls sent when I went into a lobby: an older poll does not know I am waiting */
  private waitedAfter = -1;

  constructor(deps: EngineDeps) {
    this.d = {
      transport: deps.transport,
      getDisplayMedia:
        deps.getDisplayMedia === undefined
          ? typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getDisplayMedia === "function"
            ? () => navigator.mediaDevices.getDisplayMedia({ video: true, audio: false })
            : null
          : deps.getDisplayMedia,
      createPeer:
        deps.createPeer ?? ((cfg) => new RTCPeerConnection({ iceServers: cfg.iceServers }) as unknown as PeerLike),
      getUserMedia:
        deps.getUserMedia ??
        ((c) => {
          if (!navigator.mediaDevices?.getUserMedia) return Promise.reject(new Error("no-media-devices"));
          return navigator.mediaDevices.getUserMedia(c);
        }),
      newStream: deps.newStream ?? (() => (typeof MediaStream === "undefined" ? null : new MediaStream())),
      isHidden: deps.isHidden ?? (() => typeof document !== "undefined" && document.hidden),
      setTimer: deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms)),
      clearTimer: deps.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>)),
      now: deps.now ?? (() => Date.now()),
      guessMe: deps.guessMe,
    };
  }

  /* ── listening ──────────────────────────────────────────────────────── */

  getState(): EngineState {
    return this.state;
  }

  subscribe(fn: (s: EngineState) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private set(patch: Partial<EngineState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l(this.state);
  }

  private publishPeers() {
    const peers: Record<string, PeerView> = {};
    for (const [id, p] of this.peers) peers[id] = { status: p.status, stream: p.stream };
    for (const id of this.failed) if (!peers[id]) peers[id] = { status: "failed", stream: null };
    this.set({ peers });
  }

  /* ── the poll ───────────────────────────────────────────────────────── */

  start(): void {
    if (this.running) return;
    this.running = true;
    void this.tick();
  }

  stop(): void {
    this.running = false;
    if (this.timer != null) this.d.clearTimer(this.timer);
    this.timer = null;
    for (const id of this.peers.keys()) this.closePeer(id);
    this.releaseMedia();
  }

  /** Poll now — after a button, so the screen does not wait out the cadence. */
  pollNow(): void {
    if (!this.running) return;
    if (this.timer != null) this.d.clearTimer(this.timer);
    this.timer = null;
    void this.tick();
  }

  private cadence(): number {
    if (this.state.call || this.state.waiting) return CADENCE.inCall;
    if (this.d.isHidden()) return CADENCE.hidden;
    return CADENCE.idle;
  }

  private schedule(ms: number) {
    if (!this.running) return;
    if (this.timer != null) this.d.clearTimer(this.timer);
    this.timer = this.d.setTimer(() => {
      this.timer = null;
      void this.tick();
    }, ms);
  }

  private failures = 0;

  private async tick(): Promise<void> {
    if (!this.running || this.polling) return;
    this.polling = true;
    try {
      const seq = ++this.sent;
      const poll = await this.d.transport.poll(this.after, this.afterMsg);
      this.failures = 0;
      await this.apply(poll, seq);
      this.schedule(this.cadence());
    } catch (e) {
      const st = status(e);
      if (st === 401 || st === 403) {
        /* this sign-in is not on staff calls: say so once and stop asking */
        this.running = false;
        this.set({ available: false });
        return;
      }
      this.failures += 1;
      this.schedule(Math.min(30000, this.cadence() * 2 ** Math.min(this.failures, 4)));
    } finally {
      this.polling = false;
    }
  }

  /** Who I am: the platform's word first, then the call's, then the directory's guess. */
  private learnMe(poll: CallPoll) {
    const me = poll.me ?? [...poll.calls, ...poll.incoming].find((c) => c.me.principalId)?.me.principalId;
    if (me && me !== this.state.me) this.set({ me });
  }

  private resolveMe(call: Call): string | undefined {
    if (this.state.me) return this.state.me;
    const me =
      call.me.principalId ?? (this.startedId === call.id ? call.startedBy : undefined) ?? this.d.guessMe?.(call);
    if (me) this.set({ me });
    return me;
  }

  private async apply(poll: CallPoll, seq: number): Promise<void> {
    this.learnMe(poll);
    if (poll.dnd) this.set({ dnd: poll.dnd });
    if (poll.meetings) this.set({ meetings: poll.meetings });
    /* the lobby, as the platform keeps it: a page opened while waiting finds its wait; a
       wait the platform no longer lists (turned away, gone stale) ends here */
    if (poll.waiting) {
      const w = this.state.waiting;
      const listed = w ? poll.waiting.find((c) => c.id === w.id) : poll.waiting[0];
      const inCall = w && poll.calls.some((c) => c.id === w.id);
      if (listed) this.set({ waiting: listed });
      else if (w && !inCall && seq > this.waitedAfter) {
        this.set({ waiting: null });
        if (!this.state.call) this.releaseMedia();
      }
    }
    const current = this.state.call;
    const joined = poll.calls.filter((c) => !this.left.has(c.id));
    const mine = (current && joined.find((c) => c.id === current.id)) ?? joined[0] ?? null;
    const incoming = poll.incoming.filter((c) => c.id !== mine?.id && c.state !== "ended");

    if (current && !mine) {
      /* the call is gone from my list: it ended, or I was dropped. Keep what
         I started until the poll has seen it once — a poll sent before the
         create was answered does not know about it yet. */
      if (this.startedId === current.id && seq <= this.startedAfter) {
        this.set({ incoming });
      } else {
        this.finish({ ...current, state: "ended" });
        this.set({ incoming });
      }
    } else if (mine) {
      if (this.startedId === mine.id) this.startedId = null;
      /* the lobby let me in: the call I waited for is now mine */
      const admitted = this.state.waiting?.id === mine.id;
      const waiting = admitted ? null : this.state.waiting;
      this.set({ call: mine, incoming, waiting });
      this.resolveMe(mine);
      this.reconcile(mine);
      void this.loadChat(mine.id);
      if (admitted) void this.announceMedia(mine);
    } else {
      this.set({ incoming });
    }

    this.takeMessages(poll.messages ?? []);
    if (poll.lastMessage) this.afterMsg = Math.max(this.afterMsg, poll.lastMessage);

    const signals = poll.signals.filter((s) => s.id > this.after);
    this.after = Math.max(this.after, poll.lastSignal, ...signals.map((s) => s.id));
    for (const s of signals) {
      this.chain = this.chain.then(() => this.onSignal(s)).catch(() => undefined);
    }
    await this.chain;
  }

  /** Make the connections match who is on the call. */
  private reconcile(call: Call) {
    const me = this.state.me;
    if (!me || call.me.state !== "joined") return;
    for (const p of call.participants) {
      if (p.principalId === me) continue;
      const entry = this.peers.get(p.principalId);
      if (p.state === "joined" && !p.simulated) {
        if (!entry && !this.failed.has(p.principalId) && isOfferer(me, p.principalId))
          void this.offer(call.id, p.principalId, false);
      } else if (p.state !== "ringing") {
        this.failed.delete(p.principalId);
        if (entry) this.closePeer(p.principalId);
      }
    }
    this.publishPeers();
  }

  /* ── connections ────────────────────────────────────────────────────── */

  private async ice(): Promise<IceConfig> {
    if (this.iceConfig) return this.iceConfig;
    try {
      this.iceConfig = await this.d.transport.ice();
    } catch {
      this.iceConfig = { iceServers: [], relay: false };
    }
    return this.iceConfig;
  }

  private async newPeer(callId: string, other: string, offerer: boolean, retried: boolean): Promise<PeerEntry> {
    const cfg = await this.ice();
    const pc = this.d.createPeer({ iceServers: cfg.iceServers });
    const entry: PeerEntry = { pc, offerer, retried, queue: [], stream: this.d.newStream(), status: "connecting" };
    pc.onicecandidate = (e) => {
      if (e.candidate) void this.d.transport.signal(callId, other, "ice", e.candidate.toJSON()).catch(() => undefined);
    };
    pc.ontrack = (e) => {
      const s = entry.stream;
      if (s) {
        for (const t of s.getTracks()) if (t.kind === e.track.kind && t !== e.track) s.removeTrack(t);
        if (!s.getTracks().includes(e.track)) s.addTrack(e.track);
      }
      this.publishPeers();
    };
    pc.onconnectionstatechange = () => {
      if (this.peers.get(other) !== entry) return;
      const st = pc.connectionState;
      if (st === "connected") entry.status = "connected";
      else if (st === "disconnected") entry.status = "reconnecting";
      else if (st === "failed") {
        this.closePeer(other);
        if (entry.offerer && !entry.retried) {
          this.peers.set(other, { ...entry, status: "reconnecting" });
          this.publishPeers();
          this.d.setTimer(() => {
            const held = this.peers.get(other);
            if (held && held.status === "reconnecting" && held.pc === pc) this.peers.delete(other);
            const call = this.state.call;
            if (
              call &&
              call.id === callId &&
              call.participants.some((p) => p.principalId === other && p.state === "joined")
            )
              void this.offer(callId, other, true);
          }, RETRY_AFTER_MS);
          return;
        }
        this.failed.add(other);
      }
      this.publishPeers();
    };
    this.peers.set(other, entry);
    this.publishPeers();
    return entry;
  }

  /** Put my microphone and (if on) my camera on a connection's two senders. */
  private async attach(entry: PeerEntry) {
    const local = this.state.local.stream;
    const audio = local?.getAudioTracks()[0] ?? null;
    const video = this.shareTrack ?? (this.state.local.videoOn ? (local?.getVideoTracks()[0] ?? null) : null);
    await entry.audio?.sender.replaceTrack(audio);
    await entry.video?.sender.replaceTrack(video);
  }

  private async offer(callId: string, other: string, retried: boolean) {
    try {
      await this.makeOffer(callId, other, retried);
    } catch {
      /* no WebRTC here, or the platform refused the signal: show the tile as failed rather than hang */
      this.closePeer(other);
      this.failed.add(other);
      this.publishPeers();
    }
  }

  private async makeOffer(callId: string, other: string, retried: boolean) {
    const entry = await this.newPeer(callId, other, true, retried);
    entry.audio = entry.pc.addTransceiver("audio", { direction: "sendrecv" });
    entry.video = entry.pc.addTransceiver("video", { direction: "sendrecv" });
    await this.attach(entry);
    const desc = await entry.pc.createOffer();
    await entry.pc.setLocalDescription(desc);
    await this.d.transport.signal(callId, other, "offer", { sdp: desc.sdp, type: "offer" });
  }

  /** Chat lines for my call, each once, in order. */
  private takeMessages(ms: CallMessage[]) {
    const call = this.state.call;
    if (!call || !ms.length) return;
    const have = new Set(this.state.messages.map((m) => m.id));
    const fresh = ms.filter((m) => m.callId === call.id && !have.has(m.id));
    if (!fresh.length) return;
    this.set({ messages: [...this.state.messages, ...fresh].sort((a, b) => a.id - b.id) });
  }

  /** A late joiner reads what was said before they came in. */
  private async loadChat(callId: string) {
    if (this.chatFor === callId || !this.d.transport.messages) return;
    this.chatFor = callId;
    try {
      const earlier = await this.d.transport.messages(callId, 0);
      this.takeMessages(earlier);
    } catch {
      this.chatFor = null;
    }
  }

  private notify(text: string, vars?: Record<string, string>) {
    this.set({ notice: { text, vars } });
    if (this.noticeTimer != null) this.d.clearTimer(this.noticeTimer);
    this.noticeTimer = this.d.setTimer(() => this.set({ notice: undefined }), NOTICE_MS);
  }

  private nameOn(id: string | undefined): string {
    if (!id) return "";
    const c = this.state.call ?? this.state.waiting;
    return c?.participants.find((p) => p.principalId === id)?.name ?? "";
  }

  /** What the platform did to me: a host's mute, the lobby's answer, a removal, a hand-over. */
  private async onControl(p: ControlPayload) {
    const by = p.by ?? "";
    switch (p.action) {
      case "mute":
        await this.setMuted(true);
        this.notify(by ? "{name} muted you" : "The host muted you", { name: by });
        return;
      case "removed": {
        const call = this.state.call;
        if (call) {
          this.left.add(call.id);
          this.finish({ ...call, state: "ended" });
        }
        this.notify(by ? "{name} removed you from the call" : "The host removed you from this call", { name: by });
        return;
      }
      case "admitted":
        this.notify("You are in — the host let you join");
        this.pollNow();
        return;
      case "denied":
        if (this.state.waiting) {
          this.set({ waiting: null });
          if (!this.state.call) this.releaseMedia();
        }
        this.notify("The host did not let you in");
        return;
      case "host":
        if (p.to && p.to === this.state.me) this.notify("You are now the host");
        else if (p.to) this.notify("{name} is now the host", { name: this.nameOn(p.to) });
        return;
      case "share_stopped":
        if (this.shareTrack) {
          await this.stopShare();
          this.notify(by ? "{name} started sharing — yours stopped" : "Somebody else started sharing — yours stopped", {
            name: by,
          });
        }
        return;
    }
  }

  private async onSignal(s: Signal): Promise<void> {
    if (s.kind === "control") {
      await this.onControl((s.payload ?? {}) as ControlPayload);
      return;
    }
    const call = this.state.call;
    if (!call || s.callId !== call.id) return;
    const payload = (s.payload ?? {}) as { sdp?: string; type?: string };
    if (s.kind === "hangup") {
      this.closePeer(s.from);
      this.publishPeers();
      return;
    }
    if (s.kind === "offer") {
      /* a fresh offer replaces whatever was there: the other side restarted */
      const queued = this.peers.get(s.from)?.queue ?? [];
      if (this.peers.has(s.from)) this.closePeer(s.from);
      const entry = await this.newPeer(call.id, s.from, false, false);
      entry.queue.push(...queued);
      this.failed.delete(s.from);
      await entry.pc.setRemoteDescription({ type: "offer", sdp: payload.sdp });
      await this.flush(s.from, entry);
      /* the offer made the two transceivers; take them in the order it made them */
      const trs = entry.pc.getTransceivers();
      entry.audio = trs.find((t) => t.receiver.track?.kind === "audio") ?? trs[0];
      entry.video = trs.find((t) => t.receiver.track?.kind === "video") ?? trs[1];
      for (const t of [entry.audio, entry.video]) if (t) t.direction = "sendrecv";
      await this.attach(entry);
      const desc = await entry.pc.createAnswer();
      await entry.pc.setLocalDescription(desc);
      await this.d.transport.signal(call.id, s.from, "answer", { sdp: desc.sdp, type: "answer" });
      return;
    }
    if (s.kind === "answer") {
      const entry = this.peers.get(s.from);
      if (!entry || entry.pc.remoteDescription) return;
      await entry.pc.setRemoteDescription({ type: "answer", sdp: payload.sdp });
      await this.flush(s.from, entry);
      return;
    }
    if (s.kind === "ice") {
      const entry = this.peers.get(s.from);
      if (!entry) {
        /* candidates ahead of their offer: hold them on a placeholder the offer will take over */
        this.pendingIce(s.from).push(s.payload);
        return;
      }
      if (!entry.pc.remoteDescription) entry.queue.push(s.payload);
      else await entry.pc.addIceCandidate(s.payload).catch(() => undefined);
    }
  }

  private readonly early = new Map<string, unknown[]>();
  private pendingIce(from: string): unknown[] {
    let q = this.early.get(from);
    if (!q) this.early.set(from, (q = []));
    return q;
  }

  private async flush(from: string, entry: PeerEntry) {
    const early = this.early.get(from);
    if (early) {
      entry.queue.unshift(...early);
      this.early.delete(from);
    }
    const q = entry.queue.splice(0);
    for (const c of q) await entry.pc.addIceCandidate(c).catch(() => undefined);
  }

  private closePeer(id: string) {
    const e = this.peers.get(id);
    if (!e) return;
    e.pc.onicecandidate = null;
    e.pc.ontrack = null;
    e.pc.onconnectionstatechange = null;
    try {
      e.pc.close();
    } catch {
      /* already closed */
    }
    this.peers.delete(id);
  }

  /* ── my microphone and camera ───────────────────────────────────────── */

  /**
   * Ask for the microphone, and the camera for a video call. A refusal does
   * not end the call: no camera makes it a voice call, no microphone leaves
   * the person able to listen, and either is said on the window.
   */
  private async acquire(video: boolean): Promise<string[]> {
    const notes: string[] = [];
    let stream: MediaStream | null = null;
    try {
      stream = await this.d.getUserMedia({ audio: true, video });
    } catch (e) {
      if (message(e) === "no-media-devices") {
        notes.push("This browser cannot reach a microphone or camera here — it needs a secure (https) page.");
      } else if (video) {
        try {
          stream = await this.d.getUserMedia({ audio: true, video: false });
          notes.push("Camera refused — this call is voice only.");
        } catch {
          notes.push("Microphone and camera refused — you can listen but not be heard.");
        }
      } else {
        notes.push("Microphone refused — you can listen but not be heard.");
      }
    }
    const hasVideo = !!stream?.getVideoTracks().length;
    this.set({
      local: {
        stream,
        audioOn: !!stream?.getAudioTracks().length,
        videoOn: video && hasVideo,
        mode: video && hasVideo ? "video" : "audio",
        sharing: false,
        screen: null,
      },
    });
    return notes;
  }

  private releaseMedia() {
    this.dropShare();
    for (const t of this.state.local.stream?.getTracks() ?? []) t.stop();
    this.set({ local: { stream: null, audioOn: true, videoOn: false, mode: "audio", sharing: false, screen: null } });
  }

  /* ── what the person presses ────────────────────────────────────────── */

  private async run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    this.set({ busy: true, error: undefined });
    try {
      return await fn();
    } catch (e) {
      this.set({ error: message(e) });
      return undefined;
    } finally {
      this.set({ busy: false });
    }
  }

  /** Ring one person or several. */
  async call(invitees: string[], media: CallMedia, opts?: CallStartOptions): Promise<boolean> {
    if (this.state.call) {
      this.set({ error: "Finish the call you are on first." });
      return false;
    }
    const ok = await this.run(async () => {
      const mediaNotes = await this.acquire(media === "video");
      const { call, notes } = opts
        ? await this.d.transport.create(invitees, media, opts)
        : await this.d.transport.create(invitees, media);
      if (call.state === "ended") {
        /* everybody was held by Do not disturb: nothing rings, and the notes say why */
        this.releaseMedia();
        this.notify(notes.join(" ") || "Nobody could be rung.");
        void this.d.transport.leave(call.id).catch(() => undefined);
        return true;
      }
      this.startedId = call.id;
      this.startedAfter = this.sent;
      this.set({
        call,
        notes: [...notes, ...mediaNotes],
        ended: undefined,
        me: this.state.me ?? call.me.principalId ?? call.startedBy,
      });
      /* the platform made it a voice call: say so, and put the camera away */
      if (call.media === "audio" && this.state.local.videoOn) await this.setVideo(false, false);
      this.pollNow();
      return true;
    });
    if (!ok) this.releaseMedia();
    return !!ok;
  }

  /** Answer a ringing call, by voice or by video. A call I am already on is left first. */
  async accept(callId: string, video: boolean): Promise<boolean> {
    if (this.state.call && this.state.call.id !== callId) await this.hangup();
    const ok = await this.run(async () => {
      const mediaNotes = await this.acquire(video);
      const call = await this.d.transport.answer(callId, video && this.state.local.videoOn);
      const notes = [...mediaNotes];
      if (video && this.state.local.videoOn && !call.me.videoOn) {
        notes.push("Your level cannot use video — you joined by voice.");
        await this.setVideo(false, false);
      }
      this.left.delete(call.id);
      this.set({
        call,
        notes,
        ended: undefined,
        incoming: this.state.incoming.filter((c) => c.id !== callId),
      });
      this.resolveMe(call);
      this.reconcile(call);
      this.pollNow();
      return true;
    });
    if (!ok) this.releaseMedia();
    return !!ok;
  }

  /** Come into a group call going on: straight in, or into its lobby to wait for the host. */
  async join(callId: string, video = false): Promise<boolean> {
    if (this.state.call?.id === callId) return true;
    const join = this.d.transport.join;
    if (!join) {
      this.set({ error: "This platform cannot join calls yet." });
      return false;
    }
    if (this.state.call) await this.hangup();
    const ok = await this.run(async () => {
      const mediaNotes = await this.acquire(video);
      const r = await join.call(this.d.transport, callId);
      this.left.delete(callId);
      if (r.state === "waiting") {
        this.waitedAfter = this.sent;
        this.set({ waiting: r.call, notes: mediaNotes, ended: undefined });
        this.pollNow();
        return true;
      }
      this.set({ call: r.call, waiting: null, notes: mediaNotes, ended: undefined });
      this.resolveMe(r.call);
      this.reconcile(r.call);
      void this.loadChat(r.call.id);
      void this.announceMedia(r.call);
      this.pollNow();
      return true;
    });
    if (!ok) this.releaseMedia();
    return !!ok;
  }

  /**
   * Tell the platform what my microphone and camera are doing, when it does
   * not know yet. Answering carries the choice; joining and being let in from
   * the lobby do not, and the platform starts a newcomer camera-off — so
   * without this everyone drew an avatar over a picture that was arriving.
   */
  private async announceMedia(call: Call): Promise<void> {
    const local = this.state.local;
    if (call.me.audioOn === local.audioOn && call.me.videoOn === local.videoOn) return;
    const after = await this.d.transport.media(call.id, local.audioOn, local.videoOn).catch(() => null);
    if (after && this.state.call?.id === after.id) this.set({ call: after });
  }

  /** Stop waiting in a lobby. */
  async cancelWaiting(): Promise<void> {
    const w = this.state.waiting;
    if (!w) return;
    this.set({ waiting: null });
    if (!this.state.call) this.releaseMedia();
    await this.d.transport.leave(w.id).catch(() => undefined);
  }

  /** Start a meeting's call (or come into it when it is already live). */
  async startMeeting(meetingId: string, video: boolean): Promise<boolean> {
    const start = this.d.transport.startMeeting;
    if (!start) return false;
    if (this.state.call) {
      this.set({ error: "Finish the call you are on first." });
      return false;
    }
    const ok = await this.run(async () => {
      const mediaNotes = await this.acquire(video);
      let started;
      try {
        started = await start.call(this.d.transport, meetingId);
      } catch (e) {
        /* somebody else started it a moment ago: the second press joins it */
        if (status(e) !== 409) throw e;
        started = await start.call(this.d.transport, meetingId);
      }
      const { call } = started;
      const notes = started.notes ?? [];
      if (call.state === "ended" || call.me.state !== "joined") {
        this.releaseMedia();
        if (call.me.state === "waiting") {
          this.waitedAfter = this.sent;
          this.set({ waiting: call });
        } else this.notify(notes.join(" ") || "Nobody could be rung.");
        return true;
      }
      this.startedId = call.id;
      this.startedAfter = this.sent;
      this.left.delete(call.id);
      this.set({ call, notes: [...notes, ...mediaNotes], ended: undefined, me: this.state.me ?? call.me.principalId });
      this.resolveMe(call);
      this.reconcile(call);
      void this.loadChat(call.id);
      void this.announceMedia(call);
      this.pollNow();
      return true;
    });
    if (!ok) this.releaseMedia();
    return !!ok;
  }

  async decline(callId: string): Promise<void> {
    this.set({ incoming: this.state.incoming.filter((c) => c.id !== callId) });
    await this.run(() => this.d.transport.decline(callId));
  }

  /** Leave the call I am on (cancels it while it is still ringing out). */
  async hangup(): Promise<void> {
    const call = this.state.call;
    if (!call) return;
    this.left.add(call.id);
    if (this.startedId === call.id) this.startedId = null;
    this.finish(call);
    try {
      const after = await this.d.transport.leave(call.id);
      this.set({ ended: { ...after, state: "ended" } });
    } catch {
      /* the sweep ends a call whose last person stopped polling */
    }
  }

  private finish(call: Call) {
    for (const id of this.peers.keys()) this.closePeer(id);
    this.early.clear();
    this.failed.clear();
    this.releaseMedia();
    this.chatFor = null;
    this.set({ call: null, peers: {}, notes: [], ended: call, error: undefined, messages: [], chatRead: 0 });
    if (this.endedTimer != null) this.d.clearTimer(this.endedTimer);
    this.endedTimer = this.d.setTimer(() => this.set({ ended: undefined }), 4000);
  }

  async setMuted(muted: boolean): Promise<void> {
    const call = this.state.call;
    for (const t of this.state.local.stream?.getAudioTracks() ?? []) t.enabled = !muted;
    this.set({ local: { ...this.state.local, audioOn: !muted } });
    if (call) await this.d.transport.media(call.id, !muted, this.state.local.videoOn).catch(() => undefined);
  }

  /**
   * Camera on or off. On: the camera is asked for now if this call began by
   * voice, and put on every connection's video sender — no new offer, the
   * transceiver was there from the start. Off: the senders are emptied and
   * the camera released, so its light goes out.
   */
  async setVideo(on: boolean, tell = true): Promise<void> {
    const call = this.state.call;
    let stream = this.state.local.stream;
    let track: MediaStreamTrack | null = null;
    if (on) {
      track = stream?.getVideoTracks()[0] ?? null;
      if (!track) {
        try {
          const cam = await this.d.getUserMedia({ audio: false, video: true });
          track = cam.getVideoTracks()[0] ?? null;
          if (track) {
            if (stream) stream.addTrack(track);
            else stream = cam;
          }
        } catch {
          this.set({ notes: [...new Set([...this.state.notes, "Camera refused — this call is voice only."])] });
          return;
        }
      }
    } else {
      for (const t of stream?.getVideoTracks() ?? []) {
        t.stop();
        stream?.removeTrack(t);
      }
    }
    /* while the screen is shared it stays on the senders; the camera returns when sharing stops */
    if (!this.shareTrack)
      for (const e of this.peers.values()) await e.video?.sender.replaceTrack(on ? track : null).catch(() => undefined);
    this.set({
      local: {
        ...this.state.local,
        stream,
        videoOn: on && !!track,
        mode: on && track ? "video" : this.state.local.mode,
      },
    });
    if (call && tell) {
      const after = await this.d.transport.media(call.id, this.state.local.audioOn, on && !!track).catch(() => null);
      if (after && on && !after.me.videoOn) {
        this.set({
          notes: [...new Set([...this.state.notes, "Your level cannot use video — this call is voice only."])],
        });
        await this.setVideo(false, false);
      }
    }
  }

  /** Voice ↔ Video: video mode turns the camera on; voice mode turns it off and shows faces as pictures. */
  async switchMode(mode: CallMedia): Promise<void> {
    this.set({ local: { ...this.state.local, mode } });
    await this.setVideo(mode === "video");
  }

  async invite(invitees: string[]): Promise<boolean> {
    const call = this.state.call;
    if (!call || !invitees.length) return false;
    const after = await this.run(() => this.d.transport.invite(call.id, invitees));
    if (after) {
      this.set({ call: after });
      this.pollNow();
    }
    return !!after;
  }

  clearError(): void {
    this.set({ error: undefined });
  }

  clearNotice(): void {
    this.set({ notice: undefined });
  }

  /* ── v2: my screen ──────────────────────────────────────────────────── */

  /** Whether this browser can share a screen at all (phones cannot). */
  canShare(): boolean {
    return !!this.d.getDisplayMedia;
  }

  /**
   * Share my screen: the screen track goes on every connection's VIDEO sender
   * with replaceTrack — the camera stops being sent, nothing renegotiates, and
   * it works in a voice call too because the video transceiver is always there.
   */
  async startShare(): Promise<boolean> {
    const call = this.state.call;
    const get = this.d.getDisplayMedia;
    if (!call || !get || this.shareTrack) return false;
    let screen: MediaStream;
    try {
      screen = await get();
    } catch (e) {
      /* the person closed the browser's picker: nothing to say */
      const name = (e as { name?: string })?.name;
      if (name !== "NotAllowedError" && name !== "AbortError")
        this.set({ notes: [...new Set([...this.state.notes, "Screen sharing is not available in this browser."])] });
      return false;
    }
    const track = screen.getVideoTracks()[0];
    if (!track) {
      for (const t of screen.getTracks()) t.stop();
      return false;
    }
    this.shareTrack = track;
    /* the browser's own "Stop sharing" bar ends the track */
    const onEnded = () => void this.stopShare();
    this.shareEnded = onEnded;
    const tr = track as MediaStreamTrack & { addEventListener?: MediaStreamTrack["addEventListener"] };
    if (typeof tr.addEventListener === "function") tr.addEventListener("ended", onEnded);
    else tr.onended = onEnded;
    for (const e of this.peers.values()) await e.video?.sender.replaceTrack(track).catch(() => undefined);
    this.set({ local: { ...this.state.local, sharing: true, screen } });
    try {
      const after = await this.d.transport.media(call.id, this.state.local.audioOn, true, true);
      if (this.state.call?.id === after.id) this.set({ call: after });
    } catch (e) {
      /* refused — a level without video cannot share: take the screen back off the senders */
      if (status(e) === 403) {
        this.dropShare();
        const local = this.state.local;
        const cam = local.videoOn ? (local.stream?.getVideoTracks()[0] ?? null) : null;
        for (const p of this.peers.values()) await p.video?.sender.replaceTrack(cam).catch(() => undefined);
        this.set({ error: message(e) });
        return false;
      }
    }
    return true;
  }

  /** Stop sharing: the camera goes back on the senders if it was on, otherwise nothing is sent. */
  async stopShare(): Promise<void> {
    if (!this.shareTrack) return;
    this.dropShare();
    const local = this.state.local;
    const cam = local.videoOn ? (local.stream?.getVideoTracks()[0] ?? null) : null;
    for (const e of this.peers.values()) await e.video?.sender.replaceTrack(cam).catch(() => undefined);
    const call = this.state.call;
    if (call) {
      const after = await this.d.transport.media(call.id, local.audioOn, local.videoOn, false).catch(() => null);
      if (after && this.state.call?.id === after.id) this.set({ call: after });
    }
  }

  /** Let go of the screen track, without telling anybody. */
  private dropShare() {
    const track = this.shareTrack;
    if (!track) return;
    this.shareTrack = null;
    const tr = track as MediaStreamTrack & { removeEventListener?: MediaStreamTrack["removeEventListener"] };
    if (this.shareEnded && typeof tr.removeEventListener === "function")
      tr.removeEventListener("ended", this.shareEnded);
    else tr.onended = null;
    this.shareEnded = null;
    const screen = this.state.local.screen;
    for (const t of screen?.getTracks() ?? [track]) t.stop();
    this.set({ local: { ...this.state.local, sharing: false, screen: null } });
  }

  /* ── v2: in the call ────────────────────────────────────────────────── */

  /** A host action: sent, the call taken from the reply, polled again. */
  private async act(fn: ((id: string) => Promise<Call | undefined>) | undefined): Promise<boolean> {
    const call = this.state.call;
    if (!call || !fn) return false;
    const after = await this.run(() => fn(call.id));
    if (after && this.state.call?.id === after.id) this.set({ call: after });
    this.pollNow();
    return !this.state.error;
  }

  raiseHand(up: boolean): Promise<boolean> {
    const t = this.d.transport;
    return this.act(t.hand && ((id) => t.hand!(id, up)));
  }

  admit(who: string, allow: boolean): Promise<boolean> {
    const t = this.d.transport;
    return this.act(t.admit && ((id) => t.admit!(id, who, allow)));
  }

  remove(who: string): Promise<boolean> {
    const t = this.d.transport;
    return this.act(t.remove && ((id) => t.remove!(id, who)));
  }

  mute(who: string | "all"): Promise<boolean> {
    const t = this.d.transport;
    return this.act(t.mute && ((id) => t.mute!(id, who)));
  }

  setRole(who: string, role: ParticipantRole): Promise<boolean> {
    const t = this.d.transport;
    return this.act(t.role && ((id) => t.role!(id, who, role)));
  }

  settings(patch: CallSettingsPatch): Promise<boolean> {
    const t = this.d.transport;
    return this.act(t.settings && ((id) => t.settings!(id, patch)));
  }

  /** End the call for everybody — the host's red button; everyone else leaves. */
  async endForAll(): Promise<void> {
    const call = this.state.call;
    const end = this.d.transport.end;
    if (!call) return;
    if (!end) return this.hangup();
    this.left.add(call.id);
    if (this.startedId === call.id) this.startedId = null;
    this.finish(call);
    try {
      const after = await end.call(this.d.transport, call.id);
      if (after) this.set({ ended: { ...after, state: "ended" } });
    } catch {
      /* the call goes on for the others; I am off it */
      await this.d.transport.leave(call.id).catch(() => undefined);
    }
  }

  async sendMessage(text: string): Promise<boolean> {
    const call = this.state.call;
    const send = this.d.transport.sendMessage;
    const clean = text.trim();
    if (!call || !send || !clean) return false;
    const m = await this.run(() => send.call(this.d.transport, call.id, clean));
    if (m) {
      this.takeMessages([m]);
      this.set({ chatRead: Math.max(this.state.chatRead, m.id) });
    }
    return !!m;
  }

  markChatRead(): void {
    const last = this.state.messages.at(-1)?.id ?? 0;
    if (last > this.state.chatRead) this.set({ chatRead: last });
  }

  /** Do not disturb changed on this device: show it before the next poll does. */
  setDnd(d: DndState): void {
    this.set({ dnd: d });
  }
}

/** The people a window draws: everyone else on the call, ringing or joined. */
export function onCall(call: Call, me: string | undefined): Participant[] {
  return call.participants.filter((p) => p.principalId !== me && (p.state === "joined" || p.state === "ringing"));
}
